import React from 'react';
import SectionHeader from '@components/common/SectionHeader';
import InvestmentInfoTable, {
  type InvestmentInfoTableLayoutVariant,
} from './InvestmentInfoTable';
import type { InvestmentInfoBlockData } from '@app-types/investmentIndicators';
import './InvestmentInfoBlock.less';

export interface InvestmentInfoBlockProps {
  data: InvestmentInfoBlockData;
}

/**
 * Maps a block key to its scoped column-width profile.
 *
 * work_items_101: QA asked Consensus, Stock Attributes, and Technical to align
 * their value column with Basic Info. Stock Attributes reuses `basic` because it
 * needs the identical 42/18/40 profile; Consensus and Technical need
 * `period-labels-wide-value`, which widens the value column to 40% without
 * shrinking the work_items_110 period sub-label. Finance intentionally keeps
 * `period-labels` (70/30) because QA did not include it.
 */
export const getInvestmentInfoTableLayoutVariant = (
  blockKey: string,
): InvestmentInfoTableLayoutVariant | undefined => {
  switch (blockKey) {
    case 'basic':
    case 'stockAttributes':
      return 'basic';
    case 'consensus':
    case 'technical':
      return 'period-labels-wide-value';
    case 'finance':
      return 'period-labels';
    case 'technicalIndicators':
      return 'technical-signals';
    default:
      return undefined;
  }
};

/**
 * One information block on the Investment Indicators screen.
 * Renders a SectionHeader (title + help icon) plus a reusable InfoTable.
 * Header-to-table gap is 8px (matches other screens).
 */
const InvestmentInfoBlock: React.FC<InvestmentInfoBlockProps> = ({ data }) => (
  <section
    id={data.anchorId}
    className="investment-info-block"
    data-node-id={data.sectionNodeIdWeb}
  >
    <SectionHeader
      title={data.title}
      showHelp
      dataNodeId={data.headerNodeIdWeb}
      as="h3"
    />
    <InvestmentInfoTable
      rows={data.rows}
      nodeId={data.tableNodeIdWeb}
      caption={data.title}
      layoutVariant={getInvestmentInfoTableLayoutVariant(data.key)}
    />
  </section>
);

export default InvestmentInfoBlock;
